import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteConfig } from '@/mocks/siteConfig';
import { categories } from '@/mocks/games';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isGameMenuOpen, setIsGameMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsGameMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: '首页' },
    { to: '/category/qipai', label: '游戏大厅', hasDropdown: true },
    { to: '/category/qipai', label: '优惠活动' },
    { to: '/article/a1', label: '新闻资讯' },
    { to: '/about', label: '关于我们' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-gamepad-fill text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-xl ${isScrolled ? 'text-foreground-950' : 'text-background-50'}`}>
              {siteConfig.name}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setIsGameMenuOpen(true)}
                  onMouseLeave={() => setIsGameMenuOpen(false)}
                >
                  <Link
                    to={link.to}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      location.pathname.startsWith('/category')
                        ? isScrolled
                          ? 'text-primary-600 bg-primary-100/70'
                          : 'text-primary-300 bg-background-950/20'
                        : isScrolled
                          ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                          : 'text-background-50/90 hover:text-background-50 hover:bg-background-950/20'
                    }`}
                  >
                    {link.label}
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </Link>
                  {isGameMenuOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 border border-background-200 rounded-xl py-2 shadow-lg">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <div className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-md">
                            <i className={`${cat.icon} text-primary-600 text-xs`}></i>
                          </div>
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    (link.to === '/' && location.pathname === '/') ||
                    (link.to !== '/' && location.pathname.startsWith(link.to))
                      ? isScrolled
                        ? 'text-primary-600 bg-primary-100/70'
                        : 'text-primary-300 bg-background-950/20'
                      : isScrolled
                        ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                        : 'text-background-50/90 hover:text-background-50 hover:bg-background-950/20'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              isScrolled
                ? 'text-foreground-700 hover:text-primary-600 border border-background-300 hover:border-primary-300'
                : 'text-background-50 border border-background-50/40 hover:border-background-50 hover:bg-background-50/10'
            }`}>
              登录
            </button>
            <button className="px-5 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">
              立即注册
            </button>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-xl ${isScrolled ? 'text-foreground-950' : 'text-background-50'} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.label}>
                  <button
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                    onClick={() => setIsGameMenuOpen(!isGameMenuOpen)}
                  >
                    {link.label}
                    <i className={`${isGameMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                  </button>
                  {isGameMenuOpen && (
                    <div className="ml-4 mt-1 flex flex-col gap-1">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 cursor-pointer"
                        >
                          <i className={`${cat.icon} text-primary-500`}></i>
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="flex gap-2 mt-3 pt-3 border-t border-background-200">
              <button className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors">
                登录
              </button>
              <button className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">
                立即注册
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}